import { MetadataInput } from "@dashboard/graphql";
import { MessageDescriptor } from "react-intl";

import { cancelOrderDialogMessages } from "./messages";

/**
 * Public order metadata written by the Dashboard right before `orderCancel`.
 *
 * Saleor's `orderCancel` mutation carries no reason, so the reason lives in the order's
 * metadata. The ORDER_CANCELLED webhook payload is built after the cancel, which is how the
 * smtp app's cancellation email (wsm-app-platform, external_apps/smtp) reads it back.
 *
 * `wsm.cancelReason` holds the customer-facing text (a canonical English label, or the free
 * text typed for "Other"); `wsm.cancelReasonCode` holds the stable code for reporting.
 */
export const ORDER_CANCEL_REASON_METADATA_KEY = "wsm.cancelReason";
export const ORDER_CANCEL_REASON_CODE_METADATA_KEY = "wsm.cancelReasonCode";
export const OTHER_ORDER_CANCEL_REASON_CODE = "other";

export interface OrderCancelReason {
  /** Stable identifier stored in `wsm.cancelReasonCode`. */
  code: string;
  /**
   * Canonical English text stored in `wsm.cancelReason` and printed in the customer's
   * cancellation email. Deliberately not localised: it must not depend on the staff
   * member's Dashboard language.
   */
  label: string;
  /** What staff see in the Dashboard (translatable). */
  message: MessageDescriptor;
}

/**
 * Cancellation reasons shared with the customer-side flow specified in WSM6-2465 (R-04).
 * Legacy WSM 5 only offered a free-text status comment, which "Other" preserves.
 */
export const orderCancelReasons: readonly OrderCancelReason[] = [
  {
    code: "wrong_part",
    label: "Ordered the wrong part",
    message: cancelOrderDialogMessages.reasonWrongPart,
  },
  {
    code: "better_price",
    label: "Found a better price",
    message: cancelOrderDialogMessages.reasonBetterPrice,
  },
  {
    code: "no_longer_needed",
    label: "No longer needed",
    message: cancelOrderDialogMessages.reasonNoLongerNeeded,
  },
  {
    code: "delivery_too_slow",
    label: "Delivery too slow",
    message: cancelOrderDialogMessages.reasonDeliveryTooSlow,
  },
  {
    code: "changed_vehicle",
    label: "Changed vehicle",
    message: cancelOrderDialogMessages.reasonChangedVehicle,
  },
  {
    code: OTHER_ORDER_CANCEL_REASON_CODE,
    label: "Other",
    message: cancelOrderDialogMessages.reasonOther,
  },
];

export interface OrderCancelDialogFormData {
  reasonCode: string;
  /** Customer-facing reason text, exactly as it will be stored and emailed. */
  reason: string;
}

export const getOrderCancelReasonByCode = (code: string | null): OrderCancelReason | undefined =>
  orderCancelReasons.find(reason => reason.code === code);

/**
 * The customer-facing reason recorded on a cancelled order, read back from its metadata.
 * `null` for orders cancelled before reasons existed, or from outside the Dashboard.
 */
export const getOrderCancelReason = (
  metadata: Array<Pick<MetadataInput, "key" | "value">> | null | undefined,
): string | null => {
  const reason = metadata
    ?.find(item => item.key === ORDER_CANCEL_REASON_METADATA_KEY)
    ?.value.trim();

  return reason ? reason : null;
};

/**
 * Resolves the dialog's raw state into submittable form data, or `null` while the form is
 * incomplete (no reason picked, or "Other" picked without details).
 */
export const buildOrderCancelFormData = (
  reasonCode: string | null,
  otherReason: string,
): OrderCancelDialogFormData | null => {
  if (!reasonCode) {
    return null;
  }

  if (reasonCode === OTHER_ORDER_CANCEL_REASON_CODE) {
    const details = otherReason.trim();

    return details ? { reasonCode, reason: details } : null;
  }

  const reason = getOrderCancelReasonByCode(reasonCode);

  return reason ? { reasonCode, reason: reason.label } : null;
};
